import { zodResolver } from '@hookform/resolvers/zod';
import CallMadeRounded from '@mui/icons-material/CallMadeRounded';
import CallReceivedRounded from '@mui/icons-material/CallReceivedRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ContactPicker, type Contact } from '../../components/pickers';
import { PageHeader } from '../../components/states';
import { money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  autoAllocate,
  draftToLines,
  fromUnits,
  isAllocatable,
  summarize,
  targetAllocate,
  type AllocationDraft,
  type ExcessPolicy,
} from './allocation';
import { AllocationEditor, type EditorItem } from './kit/AllocationEditor';
import {
  idempotency,
  invalidateMoney,
  unwrapPayment,
  useOpenItems,
  usePaymentMethods,
} from './kit/hooks';
import { links, PURPOSES, todayIn } from './kit/labels';
import { MethodSelect } from './kit/MethodSelect';
import { BackLink, SectionCard } from './kit/ui';
import { paymentSchema, type PaymentFormValues } from './schemas';

type FormPurpose = 'RECEIPT' | 'SUPPLIER_PAYMENT';
type Mode =
  { kind: 'auto' } | { kind: 'target'; id: string } | { kind: 'manual'; draft: AllocationDraft };

/** "Receive payment" and "Pay supplier": party, amount, method, then which documents it settles. */
export function PaymentFormPage() {
  const [params, setParams] = useSearchParams();
  const purpose: FormPurpose =
    params.get('purpose') === 'SUPPLIER_PAYMENT' ? 'SUPPLIER_PAYMENT' : 'RECEIPT';
  const meta = PURPOSES[purpose];
  const { format, settings } = useShop();
  const d = format.currencyDecimals;
  const policy: ExcessPolicy = settings?.excessPaymentPolicy ?? 'KEEP_AS_CREDIT';
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const methods = usePaymentMethods();

  const presetContact = params.get('contactId');
  const presetInvoice = params.get('invoiceId');
  const [contact, setContact] = useState<Contact | null>(null);
  const [mode, setMode] = useState<Mode>(
    presetInvoice ? { kind: 'target', id: presetInvoice } : { kind: 'auto' },
  );

  // A contact passed in the URL (from a customer or invoice screen).
  const preset = useQuery({
    queryKey: ['contacts', 'detail', presetContact],
    queryFn: () =>
      unwrap(api.GET('/contacts/{id}', { params: { path: { id: presetContact ?? '' } } })),
    enabled: Boolean(presetContact),
  });
  const party: Contact | null = contact ?? preset.data ?? null;

  const schema = useMemo(() => paymentSchema(d), [d]);
  const cash = methods.data?.data.find((m) => m.kind === 'CASH' && m.isActive);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    resolver: zodResolver(schema),
    values: {
      contactId: party?.id ?? '',
      amount: '',
      methodId: cash?.id ?? '',
      paymentDate: todayIn(format.timeZone),
      reference: '',
      notes: '',
    },
    resetOptions: { keepDirtyValues: true },
  });
  const amount = useWatch({ control, name: 'amount' });

  const openItems = useOpenItems(party?.id, meta.ledger);
  const items = useMemo<EditorItem[]>(
    () => (openItems.data?.claims ?? []).filter((c) => isAllocatable(c.type)),
    [openItems.data],
  );
  const target = mode.kind === 'target' ? items.find((i) => i.id === mode.id) : undefined;

  const draft: AllocationDraft =
    mode.kind === 'manual'
      ? mode.draft
      : mode.kind === 'target'
        ? targetAllocate(amount, items, mode.id, policy, d)
        : autoAllocate(amount, items, d);
  const summary = summarize(amount || '0', draft, items);

  const save = useMutation({
    mutationFn: (v: PaymentFormValues) =>
      unwrapPayment(
        api.POST('/payments', {
          params: idempotency(),
          body: {
            purpose,
            contactId: v.contactId,
            amount: v.amount,
            methodId: v.methodId,
            paymentDate: v.paymentDate,
            reference: v.reference || null,
            notes: v.notes || null,
            ...(mode.kind === 'target' ? { invoiceId: mode.id } : {}),
            ...(mode.kind === 'manual'
              ? {
                  allocations: draftToLines(mode.draft, items, d).map((l) => ({
                    target: {
                      type: l.item.type as 'SALES_INVOICE' | 'SUPPLIER_INVOICE' | 'OPENING_BALANCE',
                      id: l.item.id,
                    },
                    amount: l.amount,
                  })),
                }
              : {}),
          },
        }),
      ),
    onSuccess: async (p) => {
      notify.success(
        `${p.number} recorded — ${money(p.allocated, format)} allocated${
          Number(p.unallocated) > 0 ? `, ${money(p.unallocated, format)} kept as credit` : ''
        }`,
      );
      await invalidateMoney(qc);
      void navigate(links.payment(p.id));
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const det of e.details) {
          const field = det.path.split('.').pop();
          if (
            field === 'amount' ||
            field === 'methodId' ||
            field === 'paymentDate' ||
            field === 'reference' ||
            field === 'contactId'
          )
            setError(field, { message: det.message });
        }
      notify.error(e);
    },
  });

  const switchPurpose = (next: FormPurpose) => {
    setContact(null);
    setMode({ kind: 'auto' });
    setParams({ purpose: next }, { replace: true });
  };

  const edit = (id: string, value: string) =>
    setMode({ kind: 'manual', draft: { ...draft, [id]: value } });

  const allowed = can('payments.create');
  const totals = openItems.data;

  return (
    <>
      <BackLink to="/payments" label="Payments" />
      <PageHeader
        title={purpose === 'RECEIPT' ? 'Receive payment' : 'Pay supplier'}
        subtitle={
          purpose === 'RECEIPT'
            ? 'Record money a customer paid you and choose which invoices it settles.'
            : 'Record money you paid a supplier and choose which bills it settles.'
        }
      />
      {!allowed && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          You don’t have permission to record payments.
        </Alert>
      )}
      <form
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            if (!summary.valid) return;
            save.mutate(v);
          })(e)
        }
      >
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '400px minmax(0,1fr)' },
            alignItems: 'start',
          }}
        >
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={purpose}
                onChange={(_, v: FormPurpose | null) => v && switchPurpose(v)}
                sx={{ mb: 2.5 }}
              >
                <ToggleButton value="RECEIPT" sx={{ gap: 1 }}>
                  <CallReceivedRounded fontSize="small" /> Receive
                </ToggleButton>
                <ToggleButton value="SUPPLIER_PAYMENT" sx={{ gap: 1 }}>
                  <CallMadeRounded fontSize="small" /> Pay supplier
                </ToggleButton>
              </ToggleButtonGroup>
              <Stack spacing={2.25}>
                <ContactPicker
                  key={purpose}
                  type={meta.party}
                  value={party}
                  onChange={(c) => {
                    setContact(c);
                    setMode({ kind: 'auto' });
                    setValue('contactId', c?.id ?? '', { shouldValidate: Boolean(c) });
                  }}
                  error={Boolean(errors.contactId)}
                  helperText={errors.contactId?.message}
                />
                {party && totals && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip
                      size="small"
                      color={Number(totals.totalOutstanding) > 0 ? 'warning' : 'success'}
                      variant="outlined"
                      label={
                        Number(totals.totalOutstanding) > 0
                          ? `${purpose === 'RECEIPT' ? 'Owes you' : 'You owe'} ${money(totals.totalOutstanding, format)}`
                          : 'Nothing outstanding'
                      }
                    />
                    {Number(totals.totalCredit) > 0 && (
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`${money(totals.totalCredit, format)} credit on account`}
                      />
                    )}
                  </Stack>
                )}
                <TextField
                  {...register('amount')}
                  label="Amount"
                  error={Boolean(errors.amount)}
                  helperText={errors.amount?.message}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'decimal',
                      className: 'tabular',
                      style: { fontSize: '1.375rem', fontWeight: 650 },
                    },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">{format.currencyCode}</InputAdornment>
                      ),
                    },
                  }}
                />
                {totals && Number(totals.totalOutstanding) > 0 && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: '4px !important', flexWrap: 'wrap', rowGap: 1 }}
                  >
                    <Chip
                      size="small"
                      clickable
                      label={`Full balance · ${money(totals.totalOutstanding, format)}`}
                      onClick={() =>
                        setValue('amount', totals.totalOutstanding, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                    />
                    {target && (
                      <Chip
                        size="small"
                        clickable
                        label={`${target.number ?? ''} · ${money(target.open, format)}`}
                        onClick={() =>
                          setValue('amount', target.open, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      />
                    )}
                  </Stack>
                )}
                <Controller
                  control={control}
                  name="methodId"
                  render={({ field, fieldState }) => (
                    <MethodSelect
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    {...register('paymentDate')}
                    type="date"
                    label="Date"
                    fullWidth
                    error={Boolean(errors.paymentDate)}
                    helperText={errors.paymentDate?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    {...register('reference')}
                    label="Reference"
                    placeholder="Cheque no., txn id"
                    fullWidth
                    error={Boolean(errors.reference)}
                    helperText={errors.reference?.message}
                  />
                </Stack>
                <TextField
                  {...register('notes')}
                  label="Notes"
                  multiline
                  minRows={2}
                  error={Boolean(errors.notes)}
                  helperText={errors.notes?.message}
                />
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={2.5}>
            <SectionCard
              flush
              title={purpose === 'RECEIPT' ? 'Settle invoices' : 'Settle bills'}
              subtitle={
                party
                  ? `${party.name}’s open documents, oldest first`
                  : 'Choose a party to see what is open'
              }
            >
              {mode.kind === 'target' && (
                <Alert
                  icon={<InfoOutlined fontSize="inherit" />}
                  severity="info"
                  sx={{ borderRadius: 0 }}
                >
                  Paying {target?.number ?? 'the chosen document'} first.{' '}
                  {policy === 'APPLY_FIFO'
                    ? 'Anything above it goes to the oldest other documents (shop setting).'
                    : 'Anything above it stays as credit on account (shop setting).'}
                </Alert>
              )}
              {party ? (
                <AllocationEditor
                  amount={amount || '0'}
                  items={items}
                  draft={draft}
                  loading={openItems.isLoading}
                  autoActive={mode.kind === 'auto'}
                  onEdit={edit}
                  onAuto={() => setMode({ kind: 'auto' })}
                  onClear={() => setMode({ kind: 'manual', draft: {} })}
                />
              ) : (
                <Box sx={{ py: 7, px: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">
                    Pick a {meta.party} to see their open{' '}
                    {purpose === 'RECEIPT' ? 'invoices' : 'bills'}.
                  </Typography>
                </Box>
              )}
            </SectionCard>

            <Card
              sx={{
                p: 2,
                position: { lg: 'sticky' },
                bottom: { lg: 16 },
                zIndex: 1,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {summary.overAllocated
                    ? 'Allocated more than the amount'
                    : summary.remaining > 0n
                      ? `${money(fromUnits(summary.remaining, d), format)} will stay as credit`
                      : 'Fully allocated'}
                </Typography>
                <Typography variant="h4" className="tabular">
                  {money(amount && !errors.amount ? amount : 0, format)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.25}>
                <Button variant="outlined" onClick={() => void navigate('/payments')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={save.isPending}
                  disabled={!allowed || !summary.valid}
                >
                  {purpose === 'RECEIPT' ? 'Save receipt' : 'Save payment'}
                </Button>
              </Stack>
            </Card>
          </Stack>
        </Box>
      </form>
    </>
  );
}
